import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

interface ExtractedData {
    full_text?: string;
    start_date?: string;
    end_date?: string;
    rent_amount?: number;
    security_deposit?: number;
    tenant_name?: string;
    landlord_name?: string;
    property_address?: string;
    [key: string]: any; // For dynamic clauses
}

interface BulkLeaseValidationRecord {
    id: string;
    job_id: string;
    document_id: string;
    property_id: string;
    status: string;
    extracted_data: ExtractedData;
    error_message?: string;
    file_name?: string; // Assuming we can get this from the document_id or pass it
}

const BulkLeaseValidator: React.FC = () => {
    const { jobId } = useParams<{ jobId: string }>();
    const navigate = useNavigate();
    const [records, setRecords] = useState<BulkLeaseValidationRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [selectedRecordIds, setSelectedRecordIds] = useState<Set<string>>(new Set());
    const [editingCell, setEditingCell] = useState<{ recordId: string; field: string } | null>(null);
    const [isJobComplete, setIsJobComplete] = useState(false); // New state to track job completion

    useEffect(() => {
        if (!jobId) return;

        let pollInterval: NodeJS.Timeout;
        let attempts = 0;
        const maxAttempts = 20; // Poll for up to 20 * 3 seconds = 60 seconds

        const fetchValidationRecords = async () => {
            attempts++;
            // Only show loading spinner for the very first fetch, then rely on records state
            if (attempts === 1) {
                setLoading(true);
            }
            try {
                const response = await api.get(`/bulk/jobs/${jobId}`);
                const allRecordsProcessed = response.data.every((record: BulkLeaseValidationRecord) => record.status !== 'processing');
                if (response.data && response.data.length > 0) {
                    setRecords(response.data);
                    const currentPendingRecordsCount = response.data.filter((record: BulkLeaseValidationRecord) => record.status === 'pending_validation').length;

                    if (allRecordsProcessed) {
                        setIsJobComplete(true);
                        clearInterval(pollInterval); // Stop polling once all records are processed

                        if (currentPendingRecordsCount > 0) {
                            setMessage('All documents have been processed. Please review and approve.');
                        } else {
                            setMessage('All leases in this job have been reviewed.');
                        }
                    } else {
                        // Do not set intermediate processing messages
                        // setMessage(`Processing documents, please wait... (Attempt ${attempts}/${maxAttempts})`);
                    }
                } else if (attempts >= maxAttempts) {
                    setMessage('Documents are still processing or no records found after multiple attempts. Please refresh the page later.');
                    setIsJobComplete(true); // Consider it complete with a message
                    clearInterval(pollInterval); // Stop polling after max attempts
                } else {
                    // Do not set intermediate processing messages
                    // setMessage(`Processing documents, please wait... (Attempt ${attempts}/${maxAttempts})`);
                }
            } catch (err: any) {
                console.error('Error fetching bulk validation records:', err);
                setMessage(err.response?.data?.detail || 'Failed to load validation records.');
                setIsJobComplete(true); // Consider it complete with an error
                clearInterval(pollInterval); // Stop polling on error
            } finally {
                setLoading(false);
            }
        };

        fetchValidationRecords(); // Initial fetch
        pollInterval = setInterval(fetchValidationRecords, 3000); // Poll every 3 seconds

        return () => clearInterval(pollInterval); // Cleanup on unmount
    }, [jobId]);

    const handleSelectRecord = (recordId: string, isSelected: boolean) => {
        setSelectedRecordIds(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (isSelected) {
                newSelected.add(recordId);
            } else {
                newSelected.delete(recordId);
            }
            return newSelected;
        });
    };

    const handleSelectAll = (isSelected: boolean) => {
        if (isSelected) {
            const allPendingRecordIds = records.filter(record => record.status === 'pending_validation').map(record => record.id);
            setSelectedRecordIds(new Set(allPendingRecordIds));
        } else {
            setSelectedRecordIds(new Set());
        }
    };

    const handleDataChange = (recordId: string, field: string, value: any) => {
        setRecords(prevRecords =>
            prevRecords.map(record =>
                record.id === recordId
                    ? { ...record, extracted_data: { ...record.extracted_data, [field]: value } }
                    : record
            )
        );
    };

    const handleApproveAll = async () => {
        setSubmitting(true);
        setMessage('');
        try {
            const dataToApprove = records.filter(record => record.status === 'pending_validation');
            if (dataToApprove.length === 0) {
                setMessage('No pending records to approve.');
                setSubmitting(false);
                return;
            }

            await api.post(`/bulk/jobs/${jobId}/approve`, { records: dataToApprove });
            setMessage('All pending leases approved and saved successfully!');
            navigate('/leases'); // Redirect to leases page after approval
        } catch (err: any) {
            console.error('Error approving bulk leases:', err);
            setMessage(err.response?.data?.detail || 'Failed to approve leases.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleApproveSelected = async () => {
        setSubmitting(true);
        setMessage('');
        try {
            const dataToApprove = records.filter(record => selectedRecordIds.has(record.id) && record.status === 'pending_validation');
            if (dataToApprove.length === 0) {
                setMessage('No selected pending records to approve.');
                setSubmitting(false);
                return;
            }

            await api.post(`/bulk/jobs/${jobId}/approve`, { records: dataToApprove });
            setMessage('Selected pending leases approved and saved successfully!');
            setSelectedRecordIds(new Set()); // Clear selection
            // Re-fetch records to update their status
            // This will be handled by the polling mechanism, but a manual trigger might be good
            // For now, let's rely on polling to update the status
        } catch (err: any) {
            console.error('Error approving selected bulk leases:', err);
            setMessage(err.response?.data?.detail || 'Failed to approve selected leases.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditClick = (recordId: string, field: string) => {
        setEditingCell({ recordId, field });
    };

    const handleBlur = () => {
        setEditingCell(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur(); // Trigger onBlur to save changes
        }
    };

    if (loading && records.length === 0 && !isJobComplete) {
        return <div className="container mx-auto p-4">{t("bulkLeaseValidator.k1")}</div>;
    }

    // If the job is not complete and there are no records yet, or records are still processing,
    // we don't show the table immediately. The message will indicate background processing.
    if (!isJobComplete && records.length === 0) {
        return <div className="container mx-auto p-4 text-text-secondary">{t("bulkLeaseValidator.k2")}</div>;
    }

    // If the job is complete and there are no records, show a specific message
    if (isJobComplete && records.length === 0) {
        return <div className="container mx-auto p-4 text-text-secondary">{t("bulkLeaseValidator.k3")}</div>;
    }

    const commonFields = ['tenant_name', 'landlord_name', 'property_address', 'start_date', 'end_date', 'rent_amount', 'security_deposit'];
    const dynamicFields = Array.from(new Set(records.flatMap(record => Object.keys(record.extracted_data || {})))).filter(field => !commonFields.includes(field) && field !== 'full_text');
    const allFields = [...commonFields, ...dynamicFields];

    const pendingRecordsCount = records.filter(record => record.status === 'pending_validation').length;
    const allPendingSelected = pendingRecordsCount > 0 && pendingRecordsCount === selectedRecordIds.size;

    return (
        <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
            <h1 className="text-3xl font-bold mb-6 text-text-secondary">{t("bulkLeaseValidator.k4")} {jobId})</h1>

            {message && <p className={`mb-4 text-center p-2 rounded ${!message.includes('Failed') && !message.includes('Error') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{message}</p>}

            <div className="mb-4 flex justify-between items-center">
                <button
                    onClick={() => navigate('/bulk-leases')}
                    className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                    {t("bulkLeaseValidator.k5")} </button>
                {pendingRecordsCount > 0 && (
                    <div className="flex space-x-2">
                        <button
                            onClick={handleApproveSelected}
                            disabled={submitting || selectedRecordIds.size === 0}
                            className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {submitting ? t("bulkLeaseValidator.k6") : t("bulkLeaseValidator.k7")}
                        </button>
                        <button
                            onClick={handleApproveAll}
                            disabled={submitting || pendingRecordsCount === 0}
                            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                        >
                            {submitting ? t("bulkLeaseValidator.k8") : t("bulkLeaseValidator.k9")}
                        </button>
                    </div>
                )}
            </div>

            <div className="overflow-x-auto bg-bg-secondary p-4 rounded-lg shadow border border-border-primary">
                <table className="min-w-full divide-y divide-border-primary">
                    <thead className="bg-bg-tertiary">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                <input
                                    type="checkbox"
                                    checked={allPendingSelected}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    disabled={pendingRecordsCount === 0}
                                    className="form-checkbox h-4 w-4 text-primary rounded"
                                />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("bulkLeaseValidator.k10")}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("bulkLeaseValidator.k11")}</th>
                            {allFields.map(field => (
                                <th key={field} className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                    {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </th>
                            ))}
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("bulkLeaseValidator.k12")}</th>
                        </tr>
                    </thead>
                    <tbody className="bg-bg-secondary divide-y divide-border-primary">
                        {records.map(record => (
                            <tr key={record.id} className={record.status === 'failed' ? 'bg-red-100' : ''}>
                                <td className="px-6 py-4 text-sm font-medium text-text-primary">
                                    <input
                                        type="checkbox"
                                        checked={selectedRecordIds.has(record.id)}
                                        onChange={(e) => handleSelectRecord(record.id, e.target.checked)}
                                        disabled={record.status !== 'pending_validation'}
                                        className="form-checkbox h-4 w-4 text-primary rounded"
                                    />
                                </td>
                                <td className="px-6 py-4 text-sm font-medium text-text-primary">{record.file_name || record.document_id}</td>
                                <td className="px-6 py-4 text-sm text-text-secondary">{record.status}</td>
                                {allFields.map(field => (
                                    <td key={field} className="px-6 py-4 text-sm text-text-secondary" onClick={() => record.status === 'pending_validation' && handleEditClick(record.id, field)}>
                                        {editingCell?.recordId === record.id && editingCell?.field === field ? (
                                            (() => {
                                                if (field === 'tenant_name' || field === 'landlord_name' || field === 'property_address') {
                                                    return (
                                                        <textarea
                                                            value={record.extracted_data?.[field] || ''}
                                                            onChange={(e) => handleDataChange(record.id, field, e.target.value)}
                                                            onBlur={handleBlur}
                                                            onKeyDown={handleKeyDown}
                                                            className="w-full p-1 border border-border-primary rounded resize-y"
                                                            rows={2}
                                                            autoFocus
                                                        />
                                                    );
                                                } else if (field === 'rent_amount' || field === 'security_deposit') {
                                                    return (
                                                        <input
                                                            type="number"
                                                            value={record.extracted_data?.[field] || ''}
                                                            onChange={(e) => handleDataChange(record.id, field, e.target.value)}
                                                            onBlur={handleBlur}
                                                            onKeyDown={handleKeyDown}
                                                            className="w-full p-1 border border-border-primary rounded"
                                                            autoFocus
                                                        />
                                                    );
                                                } else {
                                                    return (
                                                        <input
                                                            type="text"
                                                            value={record.extracted_data?.[field] || ''}
                                                            onChange={(e) => handleDataChange(record.id, field, e.target.value)}
                                                            onBlur={handleBlur}
                                                            onKeyDown={handleKeyDown}
                                                            className="w-full p-1 border border-border-primary rounded"
                                                            autoFocus
                                                        />
                                                    );
                                                }
                                            })()
                                        ) : (
                                            record.extracted_data?.[field] || 'N/A'
                                        )}
                                    </td>
                                ))}
                                <td className="px-6 py-4 text-right text-sm font-medium">
                                    {record.status === 'failed' && (
                                        <span className="text-red-600">{t("bulkLeaseValidator.k13")} {record.error_message}</span>
                                    )}
                                    {/* Add individual approve/reject buttons if needed */}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default BulkLeaseValidator;
